import React from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { LOYALTY_TIERS } from '../../data/clientLoyalty';

/**
 * ClientScoreTimeline — area chart of the client's loyalty score over
 * time, reconstructed from the relationship event history.
 *
 * Each tier threshold (Méfiant <20, Neutre <40, Confiant <60, Fidèle <80,
 * Partenaire 80+) is rendered as a dashed reference line so the player
 * can read the trajectory at a glance.
 */
export default function ClientScoreTimeline({ points, brandColor }) {
  if (!points || points.length < 2) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-8 px-3 bg-ink-900/40 rounded border border-ink-700">
        <TrendingUp className="w-6 h-6 text-slate-600 mb-2" />
        <p className="text-[11px] text-slate-500">Pas assez d'historique pour tracer une tendance.</p>
        <p className="text-[10px] text-slate-600 mt-0.5">Quelques contrats supplémentaires et la courbe s'animera.</p>
      </div>
    );
  }
  return (
    <div className="h-44 -mx-1" data-testid="client-score-timeline">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 6, right: 10, left: -22, bottom: 0 }}>
          <defs>
            <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={brandColor} stopOpacity={0.45} />
              <stop offset="100%" stopColor={brandColor} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#64748b' }} stroke="#475569" tickFormatter={d => `J${d}`} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: '#64748b' }} stroke="#475569" ticks={[0, 20, 40, 60, 80, 100]} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const p = payload[0].payload;
              return (
                <div className="bg-ink-800 border border-ink-600 rounded px-2 py-1.5 text-[10px] shadow-lg">
                  <p className="text-slate-300 font-mono">J{p.day} — score {Math.round(p.score)}</p>
                  {p.label && (
                    <p className="text-slate-400 mt-0.5">{p.label} <span className={`font-bold ${p.delta > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>({p.delta > 0 ? '+' : ''}{p.delta})</span></p>
                  )}
                  {p.contractName && <p className="text-slate-500 italic mt-0.5">{p.contractName}</p>}
                </div>
              );
            }}
          />
          {/* Tier thresholds */}
          {LOYALTY_TIERS.filter(t => t.min > 0 && t.min < 100).map(t => (
            <ReferenceLine key={t.id} y={t.min} stroke={t.color} strokeDasharray="2 4" strokeOpacity={0.35} />
          ))}
          <Area
            type="monotone"
            dataKey="score"
            stroke={brandColor}
            strokeWidth={2}
            fill="url(#scoreGrad)"
            isAnimationActive
            animationDuration={600}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
